import { Component } from '@theme/component';
import { getWishlistStore, normalizeWishlistProduct as normalizeStoreProduct } from '@theme/levayo-wishlist-store-v1';

const BASELINES_KEY = 'levayo-wishlist-baselines-v1';
const MAX_PRODUCTS = 50;
const MAX_STORAGE_LENGTH = 120000;
const SERVICE_ORIGIN = 'https://levayo-wishlist.vercel.app';
const numericId = value => /^\d{1,20}$/.test(String(value ?? '')) ? String(value) : '';
const cents = value => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
const text = (value, limit = 255) => typeof value === 'string' ? value.slice(0, limit) : '';
const currency = value => /^[A-Z]{3}$/.test(String(value ?? '')) ? value : '';
const country = value => /^[A-Z]{2}$/.test(String(value ?? '')) ? value : '';
const market = value => /^[A-Za-z0-9:/_-]{1,100}$/.test(String(value ?? '')) ? String(value) : '';

function productUrl(value) {
  try {
    const url = new URL(value, location.origin);
    if (url.origin !== location.origin || url.username || url.password || !/\/products\/[a-z0-9][a-z0-9-]{0,254}\/?$/i.test(url.pathname)) return null;
    const variant = numericId(url.searchParams.get('variant'));
    url.search = ''; url.hash = '';
    if (variant) url.searchParams.set('variant', variant);
    return url;
  } catch { return null; }
}

function imageUrl(value) {
  if (typeof value !== 'string' || !value) return '';
  try {
    const url = new URL(value, location.origin);
    if (url.protocol !== 'https:' || url.username || url.password) return '';
    if (url.origin === location.origin && url.pathname.startsWith('/cdn/shop/')) return url.href;
    if (url.hostname === 'cdn.shopify.com' && url.pathname.startsWith('/s/files/')) return url.href;
  } catch {}
  return '';
}

export function normalizeWishlistProduct(value) { return normalizeStoreProduct(value); }

function normalizeBaseline(value) {
  if (!value || typeof value !== 'object' || !numericId(value.variantId) || cents(value.priceCents) == null || !currency(value.currency) || !country(value.country) || !market(value.marketId) || typeof value.available !== 'boolean') return null;
  return {
    variantId: String(value.variantId), priceCents: value.priceCents, currency: value.currency,
    country: value.country, marketId: String(value.marketId), available: value.available,
    restocked: value.restocked === true, observedAt: Number.isFinite(value.observedAt) ? value.observedAt : 0,
  };
}

export function observeWishlistSnapshot(previous, snapshot, now = Date.now()) {
  const current = normalizeBaseline({ ...snapshot, priceCents: snapshot.priceCents ?? snapshot.price, restocked: false, observedAt: now });
  if (!current) return null;
  const baseline = normalizeBaseline(previous);
  const sameContext = baseline && ['variantId', 'currency', 'country', 'marketId'].every(key => baseline[key] === current[key]);
  if (!sameContext) return { baseline: current, priceDrop: false, backInStock: false };
  const backInStock = current.available && (!baseline.available || baseline.restocked);
  return {
    baseline: { ...baseline, available: current.available, restocked: backInStock, observedAt: now },
    priceDrop: current.priceCents < baseline.priceCents, backInStock,
  };
}

export function normalizeWishlistSnapshot(value, product) {
  if (!value || String(value.productId) !== product.id || !numericId(value.variantId) || cents(value.price) == null || !currency(value.currency) || !country(value.country) || !market(value.marketId) || typeof value.available !== 'boolean') return null;
  const url = productUrl(value.url || product.url);
  if (!url || url.pathname.split('/').filter(Boolean).at(-1) !== value.handle) return null;
  const seen = new Set();
  const variants = [];
  if (Array.isArray(value.variants)) for (const variant of value.variants.slice(0, MAX_PRODUCTS)) {
    const id = numericId(variant?.id);
    if (!id || seen.has(id) || typeof variant.title !== 'string' || cents(variant.price) == null || typeof variant.available !== 'boolean') continue;
    seen.add(id); variants.push({ id, title: text(variant.title), price: variant.price, available: variant.available });
  }
  url.searchParams.set('variant', String(value.variantId));
  return {
    productId: product.id, handle: value.handle, title: text(value.title || product.title), url: url.href,
    image: imageUrl(value.image) || product.image, variantId: String(value.variantId), variantTitle: text(value.variantTitle),
    price: value.price, priceCents: value.price, currency: value.currency, country: value.country, marketId: String(value.marketId),
    available: value.available, variants, variantsComplete: value.variantsComplete !== false,
  };
}

/**
 * @typedef {object} WishlistRefs
 * @property {HTMLButtonElement} trigger
 * @property {HTMLDivElement} panel
 * @property {HTMLUListElement} list
 * @property {HTMLDivElement} empty
 * @property {HTMLSpanElement} count
 * @property {HTMLScriptElement} catalog
 * @property {HTMLTemplateElement} itemTemplate
 * @property {HTMLParagraphElement} error
 * @extends {Component<WishlistRefs>}
 */
class LevayoWishlist extends Component {
  requiredRefs = ['trigger', 'panel', 'list', 'empty', 'count', 'catalog', 'itemTemplate', 'error'];
  #controller;
  #products = new Map();
  #catalog = new Map();
  #baselines = new Map();
  #snapshots = new Map();
  #badges = new Map();
  #copy = {};
  #returnFocus;
  #refreshController;
  #refreshGeneration = 0;
  #editorController;
  #editorGeneration = 0;
  #editing = null;
  #alarmOpener;
  #busy = false;
  #serviceEnabled = false;
  #serviceOrigin = '';
  #configGeneration = 0;
  #storageError = false;
  #store;
  #unsubscribe;
  #scope;

  connectedCallback() { super.connectedCallback(); this.#bind(); }
  updatedCallback() { super.updatedCallback(); this.#bind(); }
  disconnectedCallback() {
    this.#unsubscribe?.(); this.#unsubscribe = null;
    this.close(); this.#controller?.abort(); this.#refreshController?.abort(); this.#editorController?.abort();
    this.#refreshGeneration++; this.#editorGeneration++; this.#busy = false; this.cancelAlarm(false);
    super.disconnectedCallback();
  }

  #bind() {
    this.#controller?.abort();
    this.#refreshController?.abort(); this.#editorController?.abort();
    this.#refreshGeneration++; this.#editorGeneration++;
    this.#editing = null; this.#busy = false;
    this.cancelAlarm(false);
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#catalog.clear();
    try {
      for (const product of JSON.parse(this.refs.catalog.textContent).slice(0, MAX_PRODUCTS)) {
        const normalized = normalizeWishlistProduct(product);
        if (normalized) this.#catalog.set(normalized.id, normalized);
      }
    } catch {}
    try { this.#copy = JSON.parse(this.querySelector('[data-wishlist-copy]')?.textContent || '{}'); } catch { this.#copy = {}; }
    this.#serviceOrigin = this.#validatedService();
    this.#serviceEnabled = false;
    this.#store ||= getWishlistStore();
    this.#store.registerCatalog([...this.#catalog.values()]);
    this.#unsubscribe?.();
    this.#unsubscribe = this.#store.subscribe(state => {
      const scope = state.mode === 'account' ? state.accountKey : state.mode;
      if (scope !== this.#scope) {
        this.#scope = scope; this.#refreshController?.abort(); this.#refreshGeneration++;
        this.#busy = false; this.cancelAlarm(false);
        this.#baselines.clear(); this.#snapshots.clear(); this.#badges.clear();
        if (state.mode === 'guest') this.#readStorage();
      }
      this.#products = new Map(state.products.map(product => [product.id, product]));
      if (state.mode === 'account') for (const product of state.products) {
        if (!this.#baselines.has(product.id)) {
          const baseline = normalizeBaseline({ ...product, variantId: product.selectedVariantId });
          if (baseline) this.#baselines.set(product.id, baseline);
        }
      }
      this.#render(); this.#renderSync(state);
    });
    void this.#store.ready();
    document.addEventListener('click', event => {
      if (event.target instanceof Element && event.target.closest('[data-wishlist-open]')) {
        const details = event.target.closest('header-drawer')?.querySelector('details');
        if (details?.open) {
          const openAfterClose = () => {
            if (details.open) return;
            details.removeEventListener('toggle', openAfterClose);
            this.open(true);
          };
          details.addEventListener('toggle', openAfterClose, { signal });
        } else this.open(true);
      }
    }, { signal });
    document.addEventListener('theme-drawer:open', () => this.close(), { signal });
    document.addEventListener('dialog:open', () => this.close(), { signal });
    this.refs.panel.addEventListener('toggle', event => {
      this.refs.trigger.setAttribute('aria-expanded', String(event.newState === 'open'));
    }, { signal });
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.refs.panel.matches(':popover-open')) {
        event.preventDefault(); event.stopPropagation(); this.closeAndFocus();
      }
    }, { signal });
    this.addEventListener('submit', event => {
      if (event.target instanceof HTMLFormElement && event.target.matches('[data-wishlist-alarm-form]')) {
        event.preventDefault(); void this.#submitAlarm(event.target);
      }
    }, { signal });
    this.querySelector('[data-wishlist-alarm-variant]')?.addEventListener('change', () => this.#renderEditorVariant(), { signal });
    window.addEventListener('resize', () => this.#position(), { passive: true, signal });
    document.addEventListener('scroll', () => this.#position(), { passive: true, capture: true, signal });
  }

  #text(key) { return typeof this.#copy[key] === 'string' ? this.#copy[key] : ''; }
  #isOpen() { return Boolean(this.refs.panel?.matches(':popover-open')); }
  #feedback(key) { const node = this.querySelector('[data-wishlist-updates]'); if (node) node.textContent = key ? this.#text(key) : ''; }
  #alarmFeedback(key) { const node = this.querySelector('[data-wishlist-alarm-status]'); if (node) node.textContent = key ? this.#text(key) : ''; }

  #validatedService() {
    if (!this.dataset.alertsUrl) return '';
    try {
      const url = new URL(this.dataset.alertsUrl);
      if (url.origin === SERVICE_ORIGIN && url.protocol === 'https:' && !url.username && !url.password && !url.search && !url.hash && url.pathname === '/') return url.origin;
    } catch {}
    return '';
  }

  #readStorage() {
    this.#baselines.clear(); this.#storageError = false;
    try {
      const raw = localStorage.getItem(BASELINES_KEY) || '{}';
      const values = raw.length <= MAX_STORAGE_LENGTH ? JSON.parse(raw) : {};
      if (values && typeof values === 'object' && !Array.isArray(values)) for (const [id, value] of Object.entries(values).slice(0, MAX_PRODUCTS)) {
        const baseline = normalizeBaseline(value); if (numericId(id) && baseline) this.#baselines.set(id, baseline);
      }
    } catch { this.#storageError = true; }
  }

  #persist() {
    if (this.#store?.snapshot().mode !== 'guest') return;
    try {
      const baselines = JSON.stringify(Object.fromEntries(this.#saved().flatMap(product => {
        const baseline = this.#baselines.get(product.id); return baseline ? [[product.id, baseline]] : [];
      })));
      if (localStorage.getItem(BASELINES_KEY) !== baselines) localStorage.setItem(BASELINES_KEY, baselines);
    } catch { this.#storageError = true; this.refs.error.hidden = false; }
  }

  #saved() { return [...this.#products.values()].slice(0, MAX_PRODUCTS); }

  #renderSync(state) {
    const note = this.querySelector('[data-wishlist-storage-note]');
    if (note) note.textContent = this.#text(state.mode === 'account' ? 'account_note' : state.mode === 'guest' ? 'guest_note' : '');
    const status = this.querySelector('[data-wishlist-sync-status]');
    if (status) status.textContent = state.pending ? this.#text('saving') : state.error ? this.#text(state.error === 'CONFLICT' ? 'conflict' : 'sync_error') : state.mode === 'checking' ? this.#text('checking_account') : '';
    const retry = this.querySelector('[data-wishlist-sync-retry]');
    if (retry) retry.hidden = !state.error;
    const legacy = this.querySelector('[data-wishlist-legacy]');
    if (legacy) legacy.hidden = state.mode !== 'account' || !state.legacyCandidates.length;
    for (const button of this.querySelectorAll('.lvwishlist-remove,[data-wishlist-import]')) button.disabled = state.pending || !['guest','account'].includes(state.mode);
    this.refs.empty.hidden = state.products.length > 0 || !['guest','account'].includes(state.mode);
    this.refs.panel.setAttribute('aria-busy', String(state.pending || state.mode === 'checking'));
  }

  retrySync() { void this.#store.refresh({ reason: 'retry' }); }
  async importLegacy() { await this.#store.importLegacy(); }

  #render() {
    let products = [];
    try { products = this.#saved(); this.refs.error.hidden = !this.#storageError; }
    catch { this.#storageError = true; this.refs.error.hidden = false; }
    const existing = new Map([...this.refs.list.children].map(item => [item.dataset.product,item]));
    const ids = new Set(products.map(product => product.id));
    for (const [id,item] of existing) if (!ids.has(id)) { item.remove(); existing.delete(id); }
    const focused = document.activeElement;
    for (const [index,product] of products.entries()) {
      const item = existing.get(product.id) || this.refs.itemTemplate.content.firstElementChild.cloneNode(true);
      item.dataset.product = product.id;
      this.#displayProduct(item, product);
      const current = this.refs.list.children[index];
      if (current !== item) this.refs.list.insertBefore(item,current || null);
    }
    if (focused?.isConnected && this.refs.list.contains(focused) && focused !== document.activeElement) focused.focus({ preventScroll:true });
    this.refs.count.textContent = String(products.length);
    this.refs.empty.hidden = products.length > 0;
    if (this.#editing && !products.some(product => product.id === this.#editing.id)) this.cancelAlarm();
    this.#position();
  }

  #format(price, code) {
    try { return new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'de-DE', { style: 'currency', currency: code }).format(price / 100); }
    catch { return ''; }
  }

  #displayProduct(item, product) {
    item.querySelector('a').href = product.url;
    item.querySelector('strong').textContent = product.title.replace(/^LEVAYO\s+/i, '');
    item.querySelector('small').textContent = product.price || '';
    const variant = item.querySelector('[data-wishlist-variant]');
    if (variant) { variant.textContent = product.variantTitle === 'Default Title' ? '' : product.variantTitle; variant.hidden = !variant.textContent; }
    const image = item.querySelector('img');
    image.hidden = !product.image;
    if (product.image) image.src = product.image;
    const badges = this.#badges.get(product.id);
    for (const [name, key] of [['price', 'price_drop'], ['stock', 'back_in_stock'], ['unavailable', 'unavailable']]) {
      const badge = item.querySelector(`[data-wishlist-badge="${name}"]`);
      if (!badge) continue;
      badge.textContent = this.#text(key);
      badge.hidden = name === 'price' ? !badges?.priceDrop : name === 'stock' ? !badges?.backInStock : product.available !== false;
    }
    const alarm = item.querySelector('[data-wishlist-alarm]');
    if (alarm) alarm.hidden = !this.#serviceEnabled;
  }

  async #loadServiceConfig() {
    if (!this.#serviceOrigin) return false;
    const controller = this.#controller;
    const generation = ++this.#configGeneration;
    try {
      const response = await fetch(`${this.#serviceOrigin}/api/config`, {
        method: 'GET', mode: 'cors', credentials: 'omit', redirect: 'error', cache: 'no-store', referrerPolicy: 'no-referrer',
        signal: AbortSignal.any([this.#controller.signal, AbortSignal.timeout(5000)]), headers: { Accept: 'application/json' },
      });
      const config = await response.json();
      if (!this.isConnected || controller !== this.#controller || controller.signal.aborted || generation !== this.#configGeneration) return false;
      this.#serviceEnabled = response.ok && config?.enrollmentEnabled === true;
    } catch {
      if (controller !== this.#controller || controller.signal.aborted || generation !== this.#configGeneration) return false;
      this.#serviceEnabled = false;
    }
    for (const button of this.querySelectorAll('[data-wishlist-alarm]')) button.hidden = !this.#serviceEnabled;
    return this.#serviceEnabled;
  }

  async #fetchSnapshot(product, selectedVariantId, signal) {
    const url = productUrl(product.url);
    if (!url) throw new Error('SNAPSHOT_FAILED');
    url.search = '';
    if (numericId(selectedVariantId)) url.searchParams.set('variant', selectedVariantId);
    url.searchParams.set('section_id', 'levayo-wishlist-snapshot');
    const response = await fetch(url.href, {
      credentials: 'same-origin', redirect: 'error', cache: 'no-store', headers: { Accept: 'text/html' },
      signal: AbortSignal.any([this.#controller.signal, signal, AbortSignal.timeout(12000)]),
    });
    if (!response.ok) throw new Error('SNAPSHOT_FAILED');
    const html = await response.text();
    if (html.length > 100000) throw new Error('SNAPSHOT_FAILED');
    const document = new DOMParser().parseFromString(html, 'text/html');
    const node = document.querySelector('script[data-wishlist-snapshot]');
    const snapshot = normalizeWishlistSnapshot(JSON.parse(node?.textContent || 'null'), product);
    if (!snapshot || (selectedVariantId && snapshot.variantId !== String(selectedVariantId))) throw new Error('SNAPSHOT_FAILED');
    if (snapshot.currency !== this.dataset.currency || snapshot.country !== this.dataset.country || (this.dataset.marketId && snapshot.marketId !== this.dataset.marketId)) throw new Error('MARKET_CHANGED');
    return snapshot;
  }

  #acceptSnapshot(product, snapshot) {
    const observation = observeWishlistSnapshot(this.#baselines.get(product.id), snapshot);
    if (!observation) return;
    const updated = normalizeWishlistProduct({ ...product, title: snapshot.title, url: snapshot.url, image: snapshot.image,
      selectedVariantId: snapshot.variantId, variantTitle: snapshot.variantTitle, priceCents: snapshot.price,
      price: this.#format(snapshot.price, snapshot.currency), currency: snapshot.currency, country: snapshot.country,
      marketId: snapshot.marketId, available: snapshot.available });
    if (!updated) return;
    this.#products.set(product.id, updated); this.#snapshots.set(product.id, snapshot);
    this.#baselines.set(product.id, observation.baseline); this.#badges.set(product.id, observation);
    const item = this.refs.list.querySelector(`[data-product="${product.id}"]`);
    if (item) this.#displayProduct(item, updated);
    this.#persist(); this.#store.observe(updated);
  }

  async #refreshSaved() {
    this.#refreshController?.abort();
    this.#refreshController = new AbortController();
    const signal = this.#refreshController.signal;
    const generation = ++this.#refreshGeneration;
    let products;
    try { products = this.#saved(); } catch { return; }
    if (!products.length) { this.#feedback(''); return; }
    this.#feedback('checking');
    let cursor = 0; let failed = false;
    const worker = async () => {
      while (cursor < products.length && !signal.aborted) {
        const product = products[cursor++];
        try {
          const snapshot = await this.#fetchSnapshot(product, product.selectedVariantId, signal);
          if (generation !== this.#refreshGeneration || !this.isConnected || signal.aborted) return;
          if (this.#store.snapshot().products.some(item => item.id === product.id)) this.#acceptSnapshot(product, snapshot);
        } catch { if (!signal.aborted) failed = true; }
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, products.length) }, worker));
    if (generation === this.#refreshGeneration && !signal.aborted) this.#feedback(failed ? 'refresh_error' : '');
  }

  toggle() {
    if (this.refs.panel.matches(':popover-open')) this.close(); else this.open();
  }

  open(mobile = false) {
    if (!this.refs.panel.showPopover) return;
    this.#returnFocus = mobile ? document.querySelector('#Details-menu-drawer-container > summary') : this.refs.trigger;
    this.#render();
    this.refs.panel.showPopover();
    this.#position();
    this.refs.panel.querySelector('button').focus({ preventScroll: true });
    void this.#loadServiceConfig();
    void this.#store.refresh({ reason: 'open' }).then(() => { if (this.#isOpen()) void this.#refreshSaved(); });
  }

  close() {
    if (this.refs.panel?.matches(':popover-open')) this.refs.panel.hidePopover();
    this.#refreshController?.abort(); this.#refreshGeneration++;
    if (!this.#busy) this.cancelAlarm(false);
  }
  closeAndFocus() { this.close(); this.#returnFocus?.focus({ preventScroll: true }); }

  /** @param {MouseEvent} event */
  async remove(event) {
    if (!(event.target instanceof Element)) return;
    const item = event.target.closest('[data-product]');
    if (!item) return;
    const next = item.nextElementSibling?.querySelector('button') || item.previousElementSibling?.querySelector('button');
    const nextId = next?.closest('[data-product]')?.dataset.product;
    try {
      const product = this.#products.get(item.dataset.product);
      const result = await this.#store.setSaved(product, false);
      if (!result.ok) return;
      this.#baselines.delete(item.dataset.product); this.#snapshots.delete(item.dataset.product); this.#badges.delete(item.dataset.product);
      this.#persist();
      this.#render();
      this.#feedback(this.#serviceOrigin ? 'remove_notice' : '');
      const focusTarget = nextId ? this.refs.list.querySelector(`[data-product="${nextId}"] button`) : this.refs.panel.querySelector('button');
      focusTarget?.focus({ preventScroll: true });
    } catch { this.refs.error.hidden = false; }
  }

  /** @param {MouseEvent} event */
  async startAlarm(event) {
    if (this.#busy || !this.#serviceEnabled || !this.#serviceOrigin || !(event.target instanceof Element)) return;
    const id = event.target.closest('[data-product]')?.dataset.product;
    const product = this.#products.get(id);
    if (!product) return;
    this.cancelAlarm(false);
    this.#alarmOpener = event.target.closest('button');
    this.#editing = { id, product, snapshot: null };
    this.#editorController = new AbortController();
    const signal = this.#editorController.signal;
    const generation = ++this.#editorGeneration;
    const editor = this.querySelector('[data-wishlist-alarm-editor]');
    const form = this.querySelector('[data-wishlist-alarm-form]');
    if (!editor || !form) return;
    editor.hidden = false; form.hidden = false; form.reset();
    this.querySelector('[data-wishlist-alarm-product]').textContent = product.title.replace(/^LEVAYO\s+/i, '');
    this.#lockEditor(true); this.#alarmFeedback('checking');
    try {
      if (!await this.#loadServiceConfig()) throw new Error('SERVICE_UNAVAILABLE');
      const snapshot = await this.#fetchSnapshot(product, product.selectedVariantId, signal);
      if (generation !== this.#editorGeneration || signal.aborted || !this.#editing) return;
      if (!snapshot.variantsComplete || !snapshot.variants.length || !snapshot.variants.some(variant => variant.id === snapshot.variantId)) throw new Error('SNAPSHOT_FAILED');
      this.#editing.snapshot = snapshot;
      const select = this.querySelector('[data-wishlist-alarm-variant]');
      select.replaceChildren();
      for (const variant of snapshot.variants) {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = (variant.title === 'Default Title' ? this.#text('standard_variant') : variant.title) + (variant.available ? '' : ` · ${this.#text('unavailable')}`);
        select.append(option);
      }
      select.value = snapshot.variantId;
      this.#renderEditorVariant(); this.#alarmFeedback('');
      this.#lockEditor(false); select.focus({ preventScroll: true });
    } catch (error) {
      if (generation !== this.#editorGeneration || signal.aborted) return;
      this.#alarmFeedback(error.message === 'SERVICE_UNAVAILABLE' ? 'unavailable_service' : error.message === 'MARKET_CHANGED' ? 'market_changed' : 'snapshot_error');
      this.#lockEditor(true);
    }
    this.#position();
    editor.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }

  #renderEditorVariant() {
    const variant = this.#editing?.snapshot?.variants.find(candidate => candidate.id === this.querySelector('[data-wishlist-alarm-variant]')?.value);
    const price = this.querySelector('[data-wishlist-alarm-price]');
    if (price) price.textContent = variant ? this.#format(variant.price, this.#editing.snapshot.currency) : '';
  }

  #lockEditor(value) {
    const form = this.querySelector('[data-wishlist-alarm-form]');
    if (!form) return;
    form.setAttribute('aria-busy', String(value));
    for (const control of form.querySelectorAll('input,select,button[type="submit"]')) control.disabled = value;
    const button = form.querySelector('[type="submit"]');
    if (button) button.textContent = this.#busy ? this.#text('submitting') : this.#text('submit');
  }

  cancelAlarm(restoreFocus = true) {
    if (this.#busy) return;
    this.#editorGeneration++; this.#editorController?.abort(); this.#editing = null;
    const editor = this.querySelector('[data-wishlist-alarm-editor]');
    if (editor) editor.hidden = true;
    this.querySelector('[data-wishlist-alarm-form]')?.reset(); this.#alarmFeedback('');
    if (restoreFocus && this.#alarmOpener?.isConnected) this.#alarmOpener.focus({ preventScroll: true });
  }

  async #submitAlarm(form) {
    if (this.#busy || !this.#editing?.snapshot || !this.#serviceEnabled) return;
    const email = text(form.elements.email?.value?.trim(), 255);
    const priceDrop = form.elements.priceDrop?.checked === true;
    const backInStock = form.elements.backInStock?.checked === true;
    const consent = form.elements.consent?.checked === true;
    if (!/^[^\s@]{1,128}@[^\s@]+\.[^\s@]+$/u.test(email) || email.length > 254) { this.#alarmFeedback('invalid_email'); return; }
    if (!priceDrop && !backInStock) { this.#alarmFeedback('choose_type'); return; }
    if (!consent) { this.#alarmFeedback('consent_required'); return; }
    const variantId = numericId(form.elements.variantId?.value);
    if (!this.#editing.snapshot.variants.some(variant => variant.id === variantId)) { this.#alarmFeedback('snapshot_error'); return; }
    const editing = this.#editing;
    const generation = this.#editorGeneration;
    this.#busy = true; this.#lockEditor(true); this.#alarmFeedback('submitting');
    try {
      if (!await this.#loadServiceConfig()) throw new Error('SERVICE_UNAVAILABLE');
      const snapshot = await this.#fetchSnapshot(editing.product, variantId, this.#editorController.signal);
      if (generation !== this.#editorGeneration || !this.isConnected) return;
      if (snapshot.currency !== 'EUR') throw new Error('UNSUPPORTED_CURRENCY');
      const response = await fetch(`${this.#serviceOrigin}/api/watches`, {
        method: 'POST', mode: 'cors', credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer',
        signal: AbortSignal.any([this.#controller.signal, AbortSignal.timeout(15000)]),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email, productHandle: snapshot.handle, variantId, country: snapshot.country,
          currency: snapshot.currency, priceDrop, backInStock, consent: true }),
      });
      const result = await response.json();
      if (generation !== this.#editorGeneration || !this.isConnected) return;
      if (!response.ok || result?.status !== 'confirmation_requested') throw new Error('SUBMIT_FAILED');
      form.reset(); form.hidden = true;
      this.#alarmFeedback('confirmation_requested');
    } catch (error) {
      if (generation === this.#editorGeneration && this.isConnected) this.#alarmFeedback(
        error.message === 'SERVICE_UNAVAILABLE' ? 'unavailable_service' : error.message === 'UNSUPPORTED_CURRENCY' ? 'unsupported_currency' : error.message === 'MARKET_CHANGED' ? 'market_changed' : 'submit_error');
    } finally {
      if (generation === this.#editorGeneration) {
        this.#busy = false;
        if (this.isConnected) this.#lockEditor(false);
      }
    }
  }

  #position() {
    if (!this.refs.panel.matches(':popover-open')) return;
    const mobile = matchMedia('(max-width: 989px)').matches;
    const rect = this.refs.trigger.getBoundingClientRect();
    const width = Math.min(380, innerWidth - 24);
    const top = mobile ? 72 : Math.max(12, Math.min(rect.bottom + 10, innerHeight - 240));
    const left = mobile ? (innerWidth - width) / 2 : Math.max(12, Math.min(rect.right - width, innerWidth - width - 12));
    this.refs.panel.style.setProperty('--lvwishlist-top', `${top}px`);
    this.refs.panel.style.setProperty('--lvwishlist-left', `${left}px`);
    this.refs.panel.style.setProperty('--lvwishlist-height', `${Math.max(120, innerHeight - top - 16)}px`);
  }
}

if (!customElements.get('levayo-wishlist')) customElements.define('levayo-wishlist', LevayoWishlist);
